// ---------------------------------------------------------------------------
// C45: Form Builder API Hooks
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

type ApiFormFieldType = "text" | "email" | "phone" | "number" | "select" | "dropdown" | "checkbox" | "radio" | "textarea" | "date" | "file" | "hidden" | "heading" | "paragraph" | "divider";
type ApiConditionOperator = "eq" | "neq" | "contains" | "exists";
const FORMS_API_BASE = "/crm/pages/forms";

interface ApiEnvelope<T> {
  data: T;
}

interface ApiListResult<T> {
  items: T[];
  total: number;
}

export interface FormField {
  id: string;
  type: "text" | "email" | "phone" | "number" | "dropdown" | "select" | "checkbox" | "radio" | "textarea" | "date" | "file" | "hidden" | "heading" | "paragraph" | "divider";
  label: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[]; // for dropdown/radio/checkbox
  validation?: { min?: number; max?: number; pattern?: string };
  // C50: Conditional logic
  condition?: {
    fieldId: string; // which field to check
    operator: "equals" | "not_equals" | "contains" | "greater_than" | "less_than" | "is_empty" | "is_not_empty";
    value?: string;
    logic?: "and" | "or"; // for multiple conditions (future)
  };
}

interface ApiConditionalRule {
  dependsOnFieldId: string;
  operator: ApiConditionOperator;
  value?: unknown;
  action: "show" | "hide";
}

interface ApiFormField {
  id: string;
  name?: string;
  label: string;
  type: ApiFormFieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  validation?: { min?: number; max?: number; pattern?: string };
  conditionalRules?: ApiConditionalRule[];
}

interface ApiFormDefinition {
  id: string;
  name: string;
  fields: ApiFormField[];
  status: "draft" | "published" | "archived";
  submissions: number;
  createdAt: string;
  updatedAt: string;
}

function normalizeCondition(
  conditionalRules: ApiConditionalRule[] | undefined,
): FormField["condition"] | undefined {
  const firstRule = conditionalRules?.[0];
  if (!firstRule) return undefined;
  if (firstRule.action !== "show") return undefined;

  const operatorMap: Record<ApiConditionOperator, NonNullable<FormField["condition"]>["operator"]> = {
    eq: "equals",
    neq: "not_equals",
    contains: "contains",
    exists: "is_not_empty",
  };

  return {
    fieldId: firstRule.dependsOnFieldId,
    operator: operatorMap[firstRule.operator],
    value: typeof firstRule.value === "string" ? firstRule.value : undefined,
  };
}

function normalizeField(field: ApiFormField): FormField {
  return {
    id: field.id,
    name: field.name,
    label: field.label,
    type: field.type === "select" ? "dropdown" : field.type,
    required: field.required,
    placeholder: field.placeholder,
    options: field.options,
    validation: field.validation,
    condition: normalizeCondition(field.conditionalRules),
  };
}

function normalizeForm(form: ApiFormDefinition): FormDefinition {
  return {
    ...form,
    fields: form.fields.map(normalizeField),
  };
}

function serializeCondition(
  condition: FormField["condition"] | undefined,
): ApiConditionalRule[] | undefined {
  if (!condition) return undefined;

  const operatorMap: Record<NonNullable<FormField["condition"]>["operator"], ApiConditionOperator> = {
    equals: "eq",
    not_equals: "neq",
    contains: "contains",
    greater_than: "exists",
    less_than: "exists",
    is_empty: "exists",
    is_not_empty: "exists",
  };

  if (!["equals", "not_equals", "contains", "is_not_empty"].includes(condition.operator)) {
    return undefined;
  }

  return [{
    dependsOnFieldId: condition.fieldId,
    operator: operatorMap[condition.operator],
    value: condition.value,
    action: "show",
  }];
}

function serializeField(field: FormField): ApiFormField {
  return {
    id: field.id,
    name: field.name ?? field.id,
    label: field.label,
    type: field.type === "dropdown" ? "select" : field.type,
    required: field.required ?? false,
    placeholder: field.placeholder,
    options: field.options,
    validation: field.validation,
    conditionalRules: serializeCondition(field.condition),
  };
}

// C50: Evaluate if a field should be visible based on its condition
export function evaluateCondition(
  field: FormField,
  answers: Record<string, string>,
): boolean {
  if (!field.condition) return true; // No condition = always visible

  const { fieldId, operator, value } = field.condition;
  const answer = answers[fieldId] ?? "";

  switch (operator) {
    case "equals": return answer === value;
    case "not_equals": return answer !== value;
    case "contains": return answer.toLowerCase().includes((value ?? "").toLowerCase());
    case "greater_than": return Number(answer) > Number(value ?? 0);
    case "less_than": return Number(answer) < Number(value ?? 0);
    case "is_empty": return answer.trim() === "";
    case "is_not_empty": return answer.trim() !== "";
    default: return true;
  }
}

export interface FormDefinition {
  id: string;
  name: string;
  fields: FormField[];
  status: "draft" | "published" | "archived";
  submissions: number;
  createdAt: string;
  updatedAt: string;
}

export function useFormList() {
  const [forms, setForms] = useState<FormDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiFetch<ApiEnvelope<ApiListResult<ApiFormDefinition>>>(FORMS_API_BASE);
      setForms(response.data.items.map(normalizeForm));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load forms");
      setForms([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { void fetchForms(); }, [fetchForms]);
  return { forms, isLoading, error, refetch: fetchForms };
}

export function useFormDetail(formId: string | undefined) {
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!formId || formId === "new") {
      setForm(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiFetch<ApiEnvelope<ApiFormDefinition>>(`${FORMS_API_BASE}/${encodeURIComponent(formId)}`)
      .then((response) => {
        if (!cancelled) {
          setForm(normalizeForm(response.data));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setForm(null);
          setError(err instanceof Error ? err.message : "Failed to load form");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [formId]);

  return { form, isLoading, error };
}

export async function saveForm(formId: string, data: { name: string; fields: FormField[] }): Promise<void> {
  await apiFetch<ApiEnvelope<ApiFormDefinition>>(`${FORMS_API_BASE}/${encodeURIComponent(formId)}`, {
    method: "PUT",
    body: JSON.stringify({
      name: data.name,
      fields: data.fields.map(serializeField),
    }),
  });
}

export async function createForm(data: { name: string }): Promise<FormDefinition> {
  const response = await apiFetch<ApiEnvelope<ApiFormDefinition>>(FORMS_API_BASE, {
    method: "POST",
    body: JSON.stringify({ name: data.name }),
  });
  return normalizeForm(response.data);
}

export async function publishForm(formId: string): Promise<void> {
  await apiFetch<ApiEnvelope<ApiFormDefinition>>(`${FORMS_API_BASE}/${encodeURIComponent(formId)}/publish`, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// C46: CRM Mapping + Scoring
// ---------------------------------------------------------------------------

export interface CrmFieldMapping {
  formFieldId: string;
  crmField: "firstName" | "lastName" | "email" | "phone" | "company" | "source" | "notes";
}

export interface ScoringRule {
  id: string;
  formFieldId: string;
  condition: "equals" | "contains" | "greaterThan" | "lessThan";
  value: string;
  points: number;
}

export interface FormCrmConfig {
  mappings: CrmFieldMapping[];
  scoringRules: ScoringRule[];
  autoCreateContact: boolean;
  workflowTriggerId?: string;
}

export async function saveFormCrmConfig(formId: string, config: FormCrmConfig): Promise<void> {
  void formId;
  void config;
}

export function useFormCrmConfig(formId: string | undefined) {
  const [config, setConfig] = useState<FormCrmConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!formId) return;
    setConfig(null);
    setIsLoading(false);
  }, [formId]);

  return { config, isLoading };
}
